import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { IconButton } from '@/components/ui/IconButton';
import { IcBell, IcChevronRight } from '@/components/ui/icons';
import { Card } from '@/components/ui/Card';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { WalletCard } from '@/components/domain/WalletCard';
import { SafetyScoreBanner } from '@/components/domain/SafetyScoreBanner';
import { NewsCard } from '@/components/domain/NewsCard';
import { CountryPicker } from '@/components/domain/CountryPicker';
import { CitiesPicker } from '@/components/domain/CitiesPicker';
import { useWallet, useWalletTransactions } from '@/api/queries/wallet';
import {
  useAutoBookingStatus,
  useCurrentLocation,
  useMe,
  useUpdateLocation,
} from '@/api/queries/user';
import { useMySubscription } from '@/api/queries/subscription';
import { useSafetyBreakdown, useSafetyCurrent } from '@/api/queries/safety-score';
import { useNews } from '@/api/queries/news';
import { useBookings } from '@/api/queries/booking';
import { formatDurationCompact, minutesUntil } from '@/utils/date';
import { centsToUsdString } from '@/utils/money';
import { useLocale } from '@/hooks/useLocale';
import { paths } from '@/routes/paths';
import { OfflineBanner } from '@/components/feedback/OfflineBanner';
import styles from './styles.module.css';

export function HomePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useLocale();

  const me = useMe();
  const wallet = useWallet();
  const txs = useWalletTransactions(1);
  const location = useCurrentLocation();
  const safety = useSafetyCurrent();
  const breakdown = useSafetyBreakdown();
  const news = useNews({ limit: 8 });
  const subscription = useMySubscription();
  const abs = useAutoBookingStatus();
  const bookings = useBookings({ status: 'hold_auto', limit: 1 });
  const updateLocation = useUpdateLocation();

  const [pickerOpen, setPickerOpen] = useState<null | 'country' | 'city'>(null);
  const [pickerCountry, setPickerCountry] = useState<string | null>(null);

  const lastTx = txs.data?.items?.[0] ?? null;
  const locationLabel = location.data
    ? `${location.data.city_name ?? ''}${location.data.city_name ? ', ' : ''}${location.data.country_code}`
    : null;

  const minBalance = centsToUsdString(abs.data?.min_balance_cents ?? 1_000_000, locale);

  const autoBooked = bookings.data?.items?.[0];
  const timeToDeparture =
    autoBooked?.depart_at && minutesUntil(autoBooked.depart_at) > 0
      ? formatDurationCompact(minutesUntil(autoBooked.depart_at), locale)
      : null;

  const showTopupBanner = useMemo(
    () => (abs.data?.gap_cents ?? 0) > 0 && subscription.data?.status === 'active',
    [abs.data, subscription.data],
  );

  const showSubscribeBanner = useMemo(
    () => !subscription.data || subscription.data.status === 'lapsed',
    [subscription.data],
  );

  const greeting = me.data
    ? `${me.data.given_name ?? ''} ${me.data.surname ? me.data.surname[0] + '.' : ''}`.trim()
    : '';

  return (
    <AppShell>
      <PageContainer paddingX="md">
        <header className={styles.header}>
          <div className={styles.profile}>
            <div className={styles.avatar} />
            <span>{greeting || t('app.name')}</span>
          </div>
          <IconButton
            aria-label={t('nav.notifications')}
            variant="soft"
            onClick={() => navigate(paths.notifications)}
          >
            <IcBell />
          </IconButton>
        </header>

        <OfflineBanner />

        <WalletCard wallet={wallet.data ?? null} loading={wallet.isLoading} lastTx={lastTx ?? undefined} />

        {showTopupBanner && (
          <Banner
            variant="warning"
            title={t('home.topup_banner.title', { amount: minBalance })}
            action={
              <Button size="sm" onClick={() => navigate(paths.walletTopup)}>
                {t('home.topup_banner.cta')}
              </Button>
            }
          />
        )}

        {showSubscribeBanner && (
          <Banner
            variant="info"
            title={t('home.subscribe_banner.title')}
            action={
              <Button size="sm" onClick={() => navigate(paths.subscribePlans)}>
                {t('home.subscribe_banner.cta')}
              </Button>
            }
          />
        )}

        {(!location.isLoading && !location.data) ||
        (!location.isLoading && !safety.isLoading && location.data && !safety.data) ? (
          // No current_location → backend returns 404 on /safety-score/me/current.
          // Prompt the user to pick a country/city so we can start scoring.
          <Card variant="light">
            <p style={{ margin: 0, marginBottom: 12 }}>{t('home.situation.no_location')}</p>
            <Button block onClick={() => setPickerOpen('country')}>
              {t('common.select')}
            </Button>
          </Card>
        ) : (
          <SafetyScoreBanner
            score={safety.data ?? null}
            loading={safety.isLoading}
            locationLabel={locationLabel ?? undefined}
            onLocationClick={() => setPickerOpen('country')}
            contributingIncidents={
              breakdown.data?.contributing_incidents?.map((c) => ({
                id: c.incident_id,
                category: c.category,
              })) ?? []
            }
            trailing={
              autoBooked ? (
                <div style={{ marginTop: 12 }}>
                  <Button block onClick={() => navigate(paths.boardingPass(autoBooked.id))}>
                    {t('home.situation.more_details')}
                  </Button>
                  {timeToDeparture && (
                    <div style={{ textAlign: 'center', marginTop: 8 }}>
                      <strong>{t('home.situation.time_to_departure')}: </strong>
                      <span style={{ color: 'var(--color-danger)' }}>{timeToDeparture}</span>
                    </div>
                  )}
                </div>
              ) : safety.data && safety.data.level !== 'green' ? (
                <Button block style={{ marginTop: 12 }} onClick={() => navigate(paths.travelFlightSearch)}>
                  {t('home.situation.book_tickets_home')}
                </Button>
              ) : null
            }
          />
        )}

        <section className={styles.feed}>
          <header className={styles.feedHeader}>
            <h2 className={styles.feedTitle}>{t('home.event_feed')}</h2>
            <button className={styles.feedAll} onClick={() => navigate(paths.newsFeed)}>
              {t('common.all')} <IcChevronRight width={14} height={14} className="reverse-on-rtl" />
            </button>
          </header>
          {news.isLoading && <Spinner />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {news.data?.items?.slice(0, 3).map((n) => (
              <NewsCard key={n.id} item={n} onClick={() => navigate(paths.newsArticle(n.id))} />
            ))}
            {!news.isLoading && !news.data?.items?.length && (
              <div style={{ color: 'var(--color-text-on-dark-muted)' }}>{t('news.empty')}</div>
            )}
          </div>
        </section>

        <BottomSheet
          open={pickerOpen !== null}
          onClose={() => setPickerOpen(null)}
          title={pickerOpen === 'country' ? t('settings.home_country') : 'City'}
        >
          {pickerOpen === 'country' && (
            <CountryPicker
              value={location.data?.country_code ?? null}
              onPick={(code) => {
                setPickerCountry(code);
                setPickerOpen('city');
                void updateLocation.mutateAsync({ country_code: code });
              }}
            />
          )}
          {pickerOpen === 'city' && pickerCountry && (
            <CitiesPicker
              countryCode={pickerCountry}
              onPick={(cityId) => {
                void updateLocation.mutateAsync({
                  country_code: pickerCountry,
                  city_id: cityId,
                });
                setPickerOpen(null);
                setPickerCountry(null);
              }}
            />
          )}
        </BottomSheet>
      </PageContainer>
    </AppShell>
  );
}
